import { IonContent, IonItem, IonLabel, IonModal } from "@ionic/react";
import React, { useState, useEffect, useRef } from "react";
import { Helmet } from "react-helmet";

const generateEmojiSets = (count: number) => {
    const baseEmojis = ["🍎", "🚗", "🐶", "⚽", "🌞", "📕", "🐘", "🍔", "✈️", "🎈",
        "🚀", "🎸", "🦄", "🎃", "🥦", "📱", "🚴", "🍉", "🐳", "🏀",
        "🎺", "🌵", "⚔️", "🎠", "🍕", "🦜", "🏕", "🚗", "📖", "🎭",
        "🛶", "🎯", "⚡", "🐟", "📡", "💎", "🍩", "🦖", "🏎", "🦔",
        "🧀", "🔑", "🎹", "🌈", "🏖", "🦅", "🎭", "🌼", "🦎", "🛷",
        "🚦", "🐙", "🦆", "🚢", "📗", "🎻", "🌊", "🛸", "🏯", "🦊",
        "🚜", "📕", "🍯", "🧊", "🍇", "🏹", "🗻", "🍟", "🚂", "🐡",
        "🎢", "📌"];

    const sets = [];

    for (let i = 0; i < count; i++) {
        const commonEmoji = baseEmojis[Math.floor(Math.random() * baseEmojis.length)];
        let oddEmoji;

        do {
            oddEmoji = baseEmojis[Math.floor(Math.random() * baseEmojis.length)];
        } while (oddEmoji === commonEmoji);

        const set = new Array(4).fill(commonEmoji);
        const oddIndex = Math.floor(Math.random() * 4);
        set[oddIndex] = oddEmoji;

        sets.push(set);
    }

    return sets;
};

const OddOneOut: React.FC = () => {
    const [questionSet, setQuestionSet] = useState<string[]>([]);
    const [correctAnswer, setCorrectAnswer] = useState<string>("");
    const [message, setMessage] = useState("");
    const [showImage, setShowImage] = useState(false);
    const modal = useRef<HTMLIonModalElement>(null);

    useEffect(() => {
        generateQuestion();
        document.title = "Odd One Out – LKG Pre-Math Concept Worksheet | Mama’s Guidance"; // Set page title
    }, []);

    function findOddOneOut(arr: string[]) {
        const countMap: { [key: string]: number } = {};

        // Count occurrences of each emoji
        arr.forEach(emoji => {
            countMap[emoji] = (countMap[emoji] || 0) + 1;
        });

        // Find the emoji that appears only once
        for (const emoji in countMap) {
            if (countMap[emoji] === 1) {
                return emoji;
            }
        }
        return ""; // If no odd one is found
    }

    const generateQuestion = () => {
        const randomSet = generateEmojiSets(1);
        setQuestionSet(randomSet[0]);
        setCorrectAnswer(findOddOneOut(randomSet[0]));
    };

    const checkAnswer = (selected: string) => {
        if (selected === correctAnswer) {
            setShowImage(true);
            setTimeout(() => {
                generateQuestion()
                setShowImage(false);
                setMessage("")
            }, 5000)
        } else {
            setShowImage(false);
            setMessage("❌ Oops! Try again.");
        }
    };

    return (

        <>
            <Helmet>
                <meta name="description" content='Help LKG learners identify patterns and differences with “Odd One Out” activities on Mama’s Guidance—designed to enhance critical thinking, observation, and categorization skills through fun and engaging visual exercises.' />
            </Helmet>
            {
                showImage === false ?
                    <>
                        <div className="contentPart additionWord pickColor matchImage match-container" style={{ textAlign: "center", padding: "20px" }}>
                            <div className="showInfo" style={{ width: '80%' }} onClick={() => modal.current?.present()}><i className="fas fa-info-circle"></i></div>
                            <IonModal
                                ref={modal}
                                trigger="open-modal"
                                initialBreakpoint={1}
                                breakpoints={[0, 0.5, 0.75, 1]}
                                handleBehavior="cycle"
                                backdropDismiss={false}  // ✅ Prevents click-to-close
                                className="full-page-modal"
                            >
                                <IonContent className="ion-padding">
                                    <div style={{ textAlign: 'right', fontSize: '25px' }} onClick={() => modal.current?.dismiss()}><i className="fas fa-3a fa-times"></i></div>
                                    <h1>Odd One Out</h1>

                                    <p>
                                        One of the simplest and most enjoyable ways to introduce logic and early classification skills to young learners is through the <strong>Odd One Out</strong> activity. This pre-math concept helps LKG children identify differences, build observation skills, and understand grouping — all through visual fun!
                                    </p>

                                    <h2>🧠 What Is “Odd One Out”?</h2>
                                    <p>
                                        In this activity, children are shown a group of 3 or 4 objects. One of them is different or doesn’t belong to the group. Children are asked to <strong>identify which one is the odd one out</strong> and explain why.
                                    </p>

                                    <p><strong>Example:</strong> 🐶 Dog, 🐱 Cat, 🐭 Mouse, 🍎 Apple → Apple is the odd one out (it&apos;s not an animal).</p>

                                    <h2>🎯 Learning Objectives</h2>
                                    <ul>
                                        <li>Improve <strong>early logic and reasoning skills</strong></li>
                                        <li>Encourage <strong>observation and attention to detail</strong></li>
                                        <li>Enhance <strong>classification and grouping skills</strong></li>
                                        <li>Build <strong>vocabulary</strong> by discussing why something is different</li>
                                    </ul>

                                    <h2>📋 What’s Inside the Worksheet?</h2>
                                    <ul>
                                        <li>Picture-based questions with <strong>familiar objects, animals, fruits, shapes, etc</strong>.</li>
                                        <li>Multiple-choice activities to identify the different one</li>
                                        <li>Fun illustrations to support visual learning</li>
                                        <li>Practice for both <strong>object recognition and logical thinking</strong></li>
                                    </ul>

                                    <h2>👩‍🏫 How to Teach “Odd One Out”</h2>
                                    <ul>
                                        <li>Show your child 3–4 toys or objects and ask, <strong>“Which one is different?”</strong></li>
                                        <li>Discuss why it is different — <strong>size, color, category, use, etc</strong>.</li>
                                        <li>Use food, books, utensils, or shapes for practical exercises</li>
                                        <li>Let your child explain their answer — this builds <strong>confidence and communication</strong></li>
                                    </ul>

                                    <h2>🔗 Related Topics</h2>
                                    <div>
                                        <IonItem routerLink="/lkg/mathematics/pre-math-concepts/big-small"
                                            routerDirection="none"
                                            lines="none"
                                            detail={true}>
                                            <IonLabel>Big - Small</IonLabel>
                                        </IonItem>
                                        <IonItem routerLink="/lkg/mathematics/pre-math-concepts/heavy-light"
                                            routerDirection="none"
                                            lines="none"
                                            detail={true}>
                                            <IonLabel>Heavy - Light</IonLabel>
                                        </IonItem>
                                        <IonItem routerLink="/lkg/mathematics/pre-math-concepts/full-empty"
                                            routerDirection="none"
                                            lines="none"
                                            detail={true}>
                                            <IonLabel>Full - Empty</IonLabel>
                                        </IonItem>
                                    </div>

                                    <h2>🌟 Mama’s Guidance Tip</h2>
                                    <p>
                                        Keep learning playful! Try placing 3 blocks and 1 spoon on a table and ask, “Which one is different?” Follow it up with, “Why do you think so?”
                                    </p>

                                    <div className="btn hero-btn" onClick={() => modal.current?.dismiss()}>Close</div>
                                </IonContent>
                            </IonModal>
                            <article className="visually-hidden">
                                <h1>Odd One Out</h1>

                                <p>
                                    One of the simplest and most enjoyable ways to introduce logic and early classification skills to young learners is through the <strong>Odd One Out</strong> activity. This pre-math concept helps LKG children identify differences, build observation skills, and understand grouping — all through visual fun!
                                </p>

                                <h2>🧠 What Is “Odd One Out”?</h2>
                                <p>
                                    In this activity, children are shown a group of 3 or 4 objects. One of them is different or doesn’t belong to the group. Children are asked to <strong>identify which one is the odd one out</strong> and explain why.
                                </p>

                                <p><strong>Example:</strong> 🐶 Dog, 🐱 Cat, 🐭 Mouse, 🍎 Apple → Apple is the odd one out (it&apos;s not an animal).</p>

                                <h2>🎯 Learning Objectives</h2>
                                <ul>
                                    <li>Improve <strong>early logic and reasoning skills</strong></li>
                                    <li>Encourage <strong>observation and attention to detail</strong></li>
                                    <li>Enhance <strong>classification and grouping skills</strong></li>
                                    <li>Build <strong>vocabulary</strong> by discussing why something is different</li>
                                </ul>

                                <h2>📋 What’s Inside the Worksheet?</h2>
                                <ul>
                                    <li>Picture-based questions with <strong>familiar objects, animals, fruits, shapes, etc</strong>.</li>
                                    <li>Multiple-choice activities to identify the different one</li>
                                    <li>Fun illustrations to support visual learning</li>
                                    <li>Practice for both <strong>object recognition and logical thinking</strong></li>
                                </ul>

                                <h2>👩‍🏫 How to Teach “Odd One Out”</h2>
                                <ul>
                                    <li>Show your child 3–4 toys or objects and ask, <strong>“Which one is different?”</strong></li>
                                    <li>Discuss why it is different — <strong>size, color, category, use, etc</strong>.</li>
                                    <li>Use food, books, utensils, or shapes for practical exercises</li>
                                    <li>Let your child explain their answer — this builds <strong>confidence and communication</strong></li>
                                </ul>

                                <h2>🔗 Related Topics</h2>
                                <div>
                                    <IonItem routerLink="/lkg/mathematics/pre-math-concepts/big-small"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Big - Small</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/lkg/mathematics/pre-math-concepts/heavy-light"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Heavy - Light</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/lkg/mathematics/pre-math-concepts/full-empty"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Full - Empty</IonLabel>
                                    </IonItem>
                                </div>

                                <h2>🌟 Mama’s Guidance Tip</h2>
                                <p>
                                    Keep learning playful! Try placing 3 blocks and 1 spoon on a table and ask, “Which one is different?” Follow it up with, “Why do you think so?”
                                </p>
                            </article>
                            <h2>Find the Odd One Out</h2>
                            <div className="optionImageDiv" style={{ margin: "20px 0", display: "flex", justifyContent: "center", gap: "20px" }}>
                                {questionSet.map((object, index) => (
                                    <div key={index} style={{ cursor: "pointer" }} onClick={() => checkAnswer(object)}>
                                        {object}
                                    </div>
                                ))}
                            </div>
                            <p>{message}</p>
                        </div>
                    </>
                    :
                    <>
                        <div className="contentPart showMiddle">
                            <img style={{ height: '150px' }} className='img-fluid' src={process.env.REACT_APP_WELDONE_LINK} />
                        </div>
                    </>
            }
        </>
    );
};

export default OddOneOut;
